import React, { useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import moment from "moment"
import { ArrowLeft, DollarSign, Calendar, TrendingUp, User, Mail, Phone } from "lucide-react"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import NavigationLink from "~/library/NavigationLink"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiWorkspaceDeals } from "~/routes"

import type Deal from "~/types/Deal"
import type Payment from "~/types/Payment"
import type PaymentItem from "~/types/PaymentItem"
import type { PayablePaymentResource } from "~/types/PayablePaymentResource"

import ResourceNotes from "~/library/ResourceNotes"
import CatchDropdown from "~/library/CatchDropdown"
import { useToast } from "~/utils/Toast"
import TasksManager from "~/views/Workspace/Tasks/TasksManager"
import ButtonGroup from "~/library/UIkit/ButtonGroup"
import ActivityFeed from "~/views/Workspace/Activities/ActivityFeed"
import PayablePaymentsSection from "~/components/payments/PayablePaymentsSection"

type dealTab = "overview" | "tasks" | "activity"

const renderDetail = (title: string, content: React.ReactNode, size = 6) => (
    <div className={`col-${size}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
            <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
    </div>
)

const formatMoney = (value?: number | string | null) => {
    if (value == null) return "—"
    const numValue = typeof value === "string" ? parseFloat(value) : value
    return `$${numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatDate = (date?: string | null) => {
    if (!date) return "—"
    return moment(date).format("MMMM DD, YYYY")
}

const formatProbability = (probability?: string | number | null) => {
    if (probability == null) return "—"
    return `${probability}%`
}

const DealView: React.FC = () => {
    const { venueSlug, stripePublishableKey } = getAppConfig()
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const toast = useToast()

    const [isDeleting, setIsDeleting] = useState(false)
    const [activeTab, setActiveTab] = useState<dealTab>("overview")

    const dealTabOptions = [
        { label: "Overview", value: "overview" as dealTab },
        { label: "Tasks", value: "tasks" as dealTab },
        { label: "Activity", value: "activity" as dealTab },
    ]

    const endpoint = apiWorkspaceDeals.show.path({ venue_slug: venueSlug, id: id! })
    const { data: deal, isLoading, refetch } = useFetchResource<{ deal: Deal }, "deal">(
        endpoint,
        "deal",
        {}
    )

    const billingContact = useMemo(() => deal?.contacts?.[0] || null, [deal])

    const paymentResource: PayablePaymentResource | null =
        deal && id
            ? {
                payableType: "deal",
                payableId: id,
                customer: billingContact
                    ? {
                        name:
                            `${billingContact.firstName || ""} ${billingContact.lastName || ""}`.trim() ||
                            "Contact",
                        email: billingContact.email,
                        phone: billingContact.phone,
                    }
                    : null,
                currency: deal.currency || "USD",
                summary: {
                    title: deal.name,
                    subtitle: deal.dealType?.name || undefined,
                    meta: deal.account?.name || undefined,
                    details: deal.expected_close_date
                        ? `Expected close date: ${formatDate(deal.expected_close_date)}`
                        : undefined,
                },
            }
            : null

    const initialPaymentItems: PaymentItem[] = useMemo(() => {
        const amount = deal?.value ? Math.round(parseFloat(deal.value) * 100) : 0

        return [
            {
                description: deal?.name || "",
                quantity: 1,
                amountCents: amount > 0 ? amount : 0,
                currencyIso: deal?.currency || "USD",
            },
        ]
    }, [deal])

    const dealPayments = useMemo(() => (((deal as any)?.payments || []) as Payment[]), [deal])

    const handleDelete = async () => {
        if (!confirm("Are you sure you want to delete this deal? This action cannot be undone.")) {
            return
        }

        setIsDeleting(true)

        try {
            const url = apiWorkspaceDeals.destroy.path({
                venue_slug: venueSlug,
                id: id!,
            })

            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            })

            const result = await response.json()

            if (result.status === "ok" || response.ok) {
                toast("Deal deleted successfully", "success")
                navigate("/deals")
            } else {
                toast(
                    `Error deleting deal. ${result.errors?.join(", ") || "Please try again"}`,
                    "danger"
                )
                setIsDeleting(false)
            }
        } catch (_error) {
            toast("Error deleting deal. Please try again", "danger")
            setIsDeleting(false)
        }
    }

    if (isLoading || !deal) {
        return <LoadingWrapper isLoading />
    }

    return (
        <LoadingWrapper isLoading={isDeleting}>
            <PageTitle
                title={
                    <div className="d-flex align-items-center">
                        <button
                            type="button"
                            className="btn btn-link p-0 me-2"
                            onClick={() => navigate("/deals")}
                            aria-label="Back to Deals"
                            title="Back to Deals"
                        >
                            <ArrowLeft />
                        </button>
                        <span>{deal.name}</span>
                    </div>
                }
            >
                <CatchDropdown
                    label="Actions"
                    className="ms-2"
                    items={[
                        {
                            label: "Edit Deal",
                            to: `/deals/${deal?.prefixId}/edit`,
                        },
                        {
                            label: "Delete Deal",
                            onClick: handleDelete,
                            className: "text-danger",
                        },
                    ]}
                />
            </PageTitle>

            <div className="row mb-3">
                <div className="col-12 mb-3">
                    <ButtonGroup
                        options={dealTabOptions}
                        selectedValue={activeTab}
                        onClick={(value) => setActiveTab(value as dealTab)}
                    />
                </div>
            </div>

            {activeTab === "overview" && (
                <div className="row">
                    <div className="col-8">
                        <CatchCard title="Deal Information">
                            <div className="row">
                                {renderDetail(
                                    "Deal Type",
                                    <span className="badge badge-lg bg-primary">{deal.dealType?.name || "—"}</span>,
                                    4
                                )}
                                {renderDetail("Stage", deal.dealStage?.name || "—", 4)}
                                {renderDetail("Owner", deal.user?.name || "—", 4)}

                                {renderDetail(
                                    "Account",
                                    deal.account ? (
                                        <NavigationLink type="accounts" to={`/accounts/${deal.account.prefixId}`}>
                                            {deal.account.name}
                                        </NavigationLink>
                                    ) : (
                                        "—"
                                    ),
                                    12
                                )}
                            </div>
                        </CatchCard>

                        {deal.salesRep && (
                            <CatchCard title="Sales Representative" className="mt-3">
                                <div className="row">
                                    {renderDetail(
                                        "Name",
                                        <div className="d-flex align-items-center gap-2">
                                            <User size={18} className="text-primary" />
                                            <span>{deal.salesRep.fullName}</span>
                                        </div>,
                                        12
                                    )}
                                    {deal.salesRep.email && renderDetail(
                                        "Email",
                                        <div className="d-flex align-items-center gap-2">
                                            <Mail size={18} className="text-info" />
                                            <a href={`mailto:${deal.salesRep.email}`}>{deal.salesRep.email}</a>
                                        </div>,
                                        6
                                    )}
                                    {deal.salesRep.phone && renderDetail(
                                        "Phone",
                                        <div className="d-flex align-items-center gap-2">
                                            <Phone size={18} className="text-success" />
                                            <a href={`tel:${deal.salesRep.phone}`}>{deal.salesRep.phone}</a>
                                        </div>,
                                        6
                                    )}
                                </div>
                            </CatchCard>
                        )}

                        <CatchCard title="Deal Metrics" className="mt-3">
                            <div className="row">
                                {renderDetail(
                                    "Value",
                                    <div className="d-flex align-items-center gap-2">
                                        <DollarSign size={18} className="text-success" />
                                        <span className="fw-bold fs-5">{formatMoney(deal.value)}</span>
                                    </div>,
                                    6
                                )}
                                {renderDetail(
                                    "Probability",
                                    <div className="d-flex align-items-center gap-2">
                                        <TrendingUp size={18} className="text-info" />
                                        <span className="fw-bold fs-5">{formatProbability(deal.probability)}</span>
                                    </div>,
                                    6
                                )}
                                {renderDetail(
                                    "Expected Close Date",
                                    <div className="d-flex align-items-center gap-2">
                                        <Calendar size={18} className="text-warning" />
                                        {formatDate(deal.expected_close_date)}
                                    </div>,
                                    12
                                )}
                            </div>
                        </CatchCard>

                        {deal.contacts && deal.contacts.length > 0 && (
                            <CatchCard title="Associated Contacts" className="mt-3">
                                <div className="list-group list-group-flush">
                                    {deal.contacts.map((contact) => (
                                        <div key={contact.id} className="list-group-item">
                                            <NavigationLink type="contacts" to={`/contacts/${contact.prefixId}`}>
                                                {contact.firstName} {contact.lastName}
                                            </NavigationLink>
                                        </div>
                                    ))}
                                </div>
                            </CatchCard>
                        )}

                        {paymentResource && (
                          <div className="mt-3">
                              <PayablePaymentsSection
                                resource={paymentResource}
                                payments={dealPayments}
                                initialPaymentItems={initialPaymentItems}
                                paymentOwner={
                                    billingContact
                                      ? {
                                          name: `${billingContact.firstName || ""} ${billingContact.lastName || ""}`.trim(),
                                          email: billingContact.email,
                                          phone: billingContact.phone,
                                      }
                                      : null
                                }
                                stripePublishableKey={stripePublishableKey}
                                onRefresh={refetch}
                                totalAmountCents={deal?.value ? Math.round(parseFloat(deal.value) * 100) : 0}
                              />
                          </div>
                        )}
                    </div>

                    <div className="col-4">
                        <CatchCard title="Notes">
                            <ResourceNotes resourceType="deal" resourceId={deal?.prefixId} />
                        </CatchCard>
                    </div>
                </div>
            )}

            {activeTab === "tasks" && (
                <div className="row">
                    <div className="col-12">
                        <TasksManager dealId={deal.prefixId} />
                    </div>
                </div>
            )}

            {activeTab === "activity" && (
                <CatchCard title="Activity" className="mt-3">
                    <ActivityFeed itemType="Deal" itemId={deal.id} />
                </CatchCard>
            )}
        </LoadingWrapper>
    )
}

export default DealView
